/** @format */

import {
	AppProductReviewState,
	InvalidResponse,
	ReviewSummary,
	selectProductReviewSummary
} from '@product-reviews/data-access';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { filter, takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { SecondaryRating } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

@Component({
	selector: 'product-reviews-secondary-rating',
	templateUrl: './review-secondary-rating.component.html',
	styleUrl: './review-secondary-rating.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule]
})
export class ReviewSecondaryRatingComponent implements OnInit, OnDestroy {
	@Input() isHighlight = false;
	secondaryRatings: SecondaryRating[] = [];
	storeSelector$ = this.store.select(selectProductReviewSummary);
	private UNSUBSCRIBE$ = new Subject<void>();

	constructor(
		private store: Store<AppProductReviewState>,
		private changeDetectorRef: ChangeDetectorRef
	) {}

	/** Life-cycle hooks */
	ngOnInit() {
		this.setupStore();
	}

	ngOnDestroy() {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	/**
	 * This method connect with the store object to get the secondary rating values provides for the Product Service
	 */
	setupStore() {
		this.storeSelector$
			.pipe(
				takeUntil(this.UNSUBSCRIBE$),
				filter((data) => !(data instanceof InvalidResponse))
			)
			.subscribe(({ secondaryRatings }: ReviewSummary) => {
				if (secondaryRatings?.length) {
					this.secondaryRatings = this.setupRatingPercentages([...secondaryRatings]);
				}
				this.changeDetectorRef.markForCheck();
			});
	}

	/**
	 * This method sort the secondary rating to display the sliders type first, and then the normal ones.
	 * Also, this method convert ranges to percentages to display correctly the values to the final user
	 */
	setupRatingPercentages(secondaryRatings: SecondaryRating[]) {
		return secondaryRatings
			.sort(({ type }: SecondaryRating) => (type === 'SLIDER' ? -1 : 1))
			.map((secondaryRating: SecondaryRating) => {
				const minValue = 0;
				const maxValue = 100;
				const currentValue = this.getRangePercentage(
					1,
					secondaryRating.valueRange,
					secondaryRating.averageRating
				);
				let middleLabel = '';

				if (secondaryRating.type === 'SLIDER') {
					middleLabel = this.getMiddleLabel(secondaryRating.valueLabel || []);
				}

				return { ...secondaryRating, minValue, maxValue, value: currentValue, middleLabel };
			});
	}

	/**
	 * This method allows to find which is the value in the middle of an array.
	 * E.g: ['a', 'b', 'c'] = 'b'.
	 */
	getMiddleLabel(labels: string[]) {
		const values = [...labels];
		const index = Math.floor((values.length - 1) / 2);
		return values[index];
	}

	/**
	 * This method allows to get the range values into percentage ones.
	 * E.g: In a range 1-3, and the value received is 2 the percentage will be 50%.
	 */
	getRangePercentage(minRange: number, maxRange: number, value: number) {
		const range = maxRange - minRange;
		const correctedStartValue = value - minRange;
		return (correctedStartValue * 100) / range;
	}
}
